import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import DataTable from "../../../components/dataTable/DataTable";
import { ApiEndpoints, reqGetNew, reqPostNew } from "../../../config/apiClient";
import RondaCadastroModal from "./componentes/RondaCadastroModal";

export default function RondasPage() {
    const [rondas, setRondas] = useState<Ronda[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [rondaSelecionada, setRondaSelecionada] = useState<number | null>(null);
    const [centrosCustoFiltro, setCentrosCustoFiltro] = useState<any[]>([]);
    const [centrosCustoSelecionados, setCentrosCustoSelecionados] = useState<any[]>([]);
    const [situacaoSelecionada, setSituacaoSelecionada] = useState<any[]>([]);

    async function carregarRondas(centros: any[] = [], situacoes: any[] = []) {
        setLoading(true);
        const response = await reqPostNew(ApiEndpoints.qrcodeRondaListar, {
            ids_centro_custo: centros.map((item: any) => Number(item.value)),
            ids_situacao: situacoes.map((item: any) => Number(item.value)),
        });
        setLoading(false);

        if (response.status !== 200 || !Array.isArray(response.data)) {
            toast.error("Não foi possível carregar as rondas.", { containerId: 1, position: "top-center", theme: "dark" });
            return;
        }
        setRondas(response.data as Ronda[]);
    }

    useEffect(() => {
        document.title = "Rondas | OB PORTUS";
        const idRondaUrl = new URLSearchParams(window.location.search).get("id_ronda") || new URLSearchParams(window.location.search).get("id");
        if (idRondaUrl && /^\d+$/.test(idRondaUrl) && Number(idRondaUrl) > 0) {
            setRondaSelecionada(Number(idRondaUrl));
            setShowModal(true);
        }
        async function carregarFiltros() {
            const response = await reqGetNew(ApiEndpoints.qrcodeFiltros);
            if (response.status === 200 && response.data?.centros_custo) {
                setCentrosCustoFiltro(
                    response.data.centros_custo.map((item: any) => ({
                        value: String(item.codigo),
                        label: `${item.codigo} - ${item.centro_custo}`,
                    })),
                );
            } else {
                toast.error("Não foi possível carregar os filtros de ronda.", { containerId: 1, position: "top-center", theme: "dark" });
            }
            await carregarRondas([], []);
        }
        void carregarFiltros();
        // eslint-disable-next-line
    }, []);

    function abrirRonda(event: any) {
        const index = Number(event.target.parentElement.getAttribute("about"));
        const ronda = rondas[index];
        if (!ronda) {
            return;
        }
        const link = `/qrcode/rondas?id_ronda=${ronda.id_ronda}`;
        if (event.button === 1) {
            window.open(link);
        } else if (event.button === 0) {
            setRondaSelecionada(ronda.id_ronda);
            setShowModal(true);
            window.history.pushState({ id_ronda: ronda.id_ronda }, "", link);
        }
    }

    function abrirCadastro() {
        setRondaSelecionada(null);
        setShowModal(true);
        window.history.pushState({ id_ronda: null }, "", "/qrcode/rondas");
    }

    function fecharModal() {
        setShowModal(false);
        setRondaSelecionada(null);
        window.history.pushState({}, "", window.location.pathname);
    }

    return (
        <div id="main">
            <div className="div-row-space-between">
                <h1 id="titleMain">Registro de Rondas</h1>
                <Button onClick={abrirCadastro}>Cadastrar</Button>
            </div>
            <br />
            <div className="div-row">
                <Select name="ids_centro_custo" id="ids_centro_custo" options={centrosCustoFiltro} value={centrosCustoSelecionados} placeholder="Centros de Custo" isMulti closeMenuOnSelect={false} onChange={(choice: any) => setCentrosCustoSelecionados(choice || [])} styles={styleSelectCentros} />
                <Select name="ids_situacao" id="ids_situacao" options={situacaoOptions} value={situacaoSelecionada} placeholder="Situação" isMulti closeMenuOnSelect={false} onChange={(choice: any) => setSituacaoSelecionada(choice || [])} styles={styleSelectSituacao} />
                <Button variant="primary" onClick={() => carregarRondas(centrosCustoSelecionados, situacaoSelecionada)} style={{ height: "40px" }} className="div-row-space-between">
                    Filtrar <span className="material-symbols-outlined">filter_alt</span>
                </Button>
            </div>
            <br />
            {loading ? <Spinner /> : <DataTable dados={rondas} tableSize="auto" defaultColumns={defaultColumns} tableMiddleClickhandler={abrirRonda} tableClickhandler={abrirRonda} defaultOrder={defaultOrder} defaultRows={23} />}
            <RondaCadastroModal show={showModal} idRonda={rondaSelecionada} onClose={fecharModal} onSaved={() => carregarRondas(centrosCustoSelecionados, situacaoSelecionada)} />
        </div>
    );
}

interface Ronda {
    id_ronda: number;
    data_criacao?: string;
    descricao: string;
    ronda_pontos: unknown[];
    id_centro_custo: number | null;
    centro_custo: number | null;
    tempo_estimado: number | null;
    situacao: number;
}

const columnHelper = createColumnHelper<Ronda>();

const defaultColumns = [
    columnHelper.accessor((row) => `#${row.id_ronda}`, { header: "ID", size: 50 }),
    columnHelper.accessor("descricao", { header: "Ronda", size: 150 }),
    columnHelper.accessor((row) => row.id_centro_custo !== null ? `${row.id_centro_custo} - ${row.centro_custo}` : "-", { header: "Centro de custo", size: 300 }),
    columnHelper.accessor((row) => (row.data_criacao ? dayjs(row.data_criacao).format("DD/MM/YYYY HH:mm:ss") : ""), { header: "Criado em", size: 180 }),
    columnHelper.accessor((row) => `${row.ronda_pontos?.length ?? 0} pontos`, { header: "Pontos", size: 120 }),
    columnHelper.accessor((row) => row.tempo_estimado ?? "-", { header: "Tempo estimado", size: 150 }),
    columnHelper.accessor((row) => (row.situacao === 1 ? "Ativa" : "Inativa"), { header: "Situação", size: 120 }),
];

const defaultOrder: any[] = [];

const situacaoOptions = [
    { value: "1", label: "Ativo" },
    { value: "0", label: "Inativo" },
];

const styleSelectSituacao = {
    control: (baseStyles: any) => ({
        ...baseStyles,
        width: "250px",
        borderColor: "#dee2e6",
        minHeight: "39px",
        marginRight: "5px",
        fontSize: "14px",
    }),
};

const styleSelectCentros = {
    control: (baseStyles: any) => ({
        ...baseStyles,
        width: "500px",
        borderColor: "#dee2e6",
        minHeight: "39px",
        marginRight: "5px",
        fontSize: "14px",
    }),
};
